import { RootState } from 'store';
import { BusinessAreaServicesState } from './ServicesState';
import Service, { Meta, BusinessAreaType } from 'model/Service';

// ===== BUSINESS AREA SPECIFIC SELECTORS =====

/**
 * Get business area specific services state
 */
export const getBusinessAreaServicesState = (
    state: RootState,
    businessArea: BusinessAreaType
): BusinessAreaServicesState => {
    return state.services.servicesByBusinessArea[businessArea];
};

/**
 * Get services for specific business area
 */
export const getServicesByBusinessArea = (state: RootState, businessArea: BusinessAreaType): Array<Service> => {
    return state.services.servicesByBusinessArea[businessArea].services;
};

/**
 * Get meta for specific business area
 */
export const getMetaByBusinessArea = (state: RootState, businessArea: BusinessAreaType): Meta | null => {
    return state.services.servicesByBusinessArea[businessArea].meta;
};

/**
 * Get loading state for specific business area
 */
export const getLoadingByBusinessArea = (state: RootState, businessArea: BusinessAreaType): boolean => {
    return state.services.servicesByBusinessArea[businessArea].loading;
};

// ===== DAYCARE SPECIFIC SELECTORS =====

/**
 * Get daycare services (optimized for Daycare components)
 */
export const getDaycareServices = (state: RootState): Array<Service> => {
    return getServicesByBusinessArea(state, 'daycare');
};

/**
 * Get daycare meta (optimized for Daycare components)
 */
export const getDaycareMeta = (state: RootState): Meta | null => {
    return getMetaByBusinessArea(state, 'daycare');
};

/**
 * Get daycare loading state (optimized for Daycare components)
 */
export const getDaycareLoading = (state: RootState): boolean => {
    return getLoadingByBusinessArea(state, 'daycare');
};

/**
 * Check if daycare has initialized (has fetched data successfully at least once)
 */
export const getDaycareHasInitialized = (state: RootState): boolean => {
    const daycareState = getBusinessAreaServicesState(state, 'daycare');
    return daycareState.services.length > 0 || daycareState.meta !== null;
};

/**
 * Check if business has ever had daycare services (prevents layout jumping)
 */
export const getDaycareHasEverHadServices = (state: RootState): boolean => {
    const daycareState = getBusinessAreaServicesState(state, 'daycare');
    const hasInitialized = getDaycareHasInitialized(state);

    // If we have services now, or if we've initialized and totalServices > 0,
    // then this business has daycare services
    return daycareState.services.length > 0 || (hasInitialized && (daycareState.meta?.totalServices || 0) > 0);
};

/**
 * Check if daycare is empty (no services and has initialized)
 */
export const getDaycareIsEmpty = (state: RootState): boolean => {
    const daycareState = getBusinessAreaServicesState(state, 'daycare');
    const hasInitialized = getDaycareHasInitialized(state);
    return hasInitialized && daycareState.services.length === 0;
};

/**
 * Check if daycare is in first load state (hasn't initialized and is loading)
 */
export const getDaycareIsFirstLoad = (state: RootState): boolean => {
    const hasInitialized = getDaycareHasInitialized(state);
    const loading = getDaycareLoading(state);
    return !hasInitialized && loading;
};

// ===== GROOMING SPECIFIC SELECTORS =====

/**
 * Get grooming services (for primary services page)
 */
export const getGroomingServices = (state: RootState): Array<Service> => {
    return getServicesByBusinessArea(state, 'grooming');
};

/**
 * Get grooming meta
 */
export const getGroomingMeta = (state: RootState): Meta | null => {
    return getMetaByBusinessArea(state, 'grooming');
};

/**
 * Get grooming loading state
 */
export const getGroomingLoading = (state: RootState): boolean => {
    return getLoadingByBusinessArea(state, 'grooming');
};

/**
 * Check if grooming has initialized (has fetched data successfully at least once)
 */
export const getGroomingHasInitialized = (state: RootState): boolean => {
    const groomingState = getBusinessAreaServicesState(state, 'grooming');
    return groomingState.services.length > 0 || groomingState.meta !== null;
};

/**
 * Check if business has ever had grooming services (prevents layout jumping)
 */
export const getGroomingHasEverHadServices = (state: RootState): boolean => {
    const groomingState = getBusinessAreaServicesState(state, 'grooming');
    const hasInitialized = getGroomingHasInitialized(state);

    // If we have services now, or if we've initialized and totalServices > 0,
    // then this business has grooming services
    return groomingState.services.length > 0 || (hasInitialized && (groomingState.meta?.totalServices || 0) > 0);
};

/**
 * Check if grooming is truly empty (no services, has initialized, and business never had services)
 */
export const getGroomingIsTrulyEmpty = (state: RootState): boolean => {
    const groomingState = getBusinessAreaServicesState(state, 'grooming');
    const hasInitialized = getGroomingHasInitialized(state);
    return hasInitialized && groomingState.meta?.totalServices === 0;
};

// ===== TRANSPORTATION SPECIFIC SELECTORS =====

/**
 * Get transportation services (optimized for Transportation components)
 */
export const getTransportationServices = (state: RootState): Array<Service> => {
    return getServicesByBusinessArea(state, 'transportation');
};

/**
 * Get transportation meta (optimized for Transportation components)
 */
export const getTransportationMeta = (state: RootState): Meta | null => {
    return getMetaByBusinessArea(state, 'transportation');
};

/**
 * Get transportation loading state (optimized for Transportation components)
 */
export const getTransportationLoading = (state: RootState): boolean => {
    return getLoadingByBusinessArea(state, 'transportation');
};

/**
 * Check if transportation has initialized (has fetched data successfully at least once)
 */
export const getTransportationHasInitialized = (state: RootState): boolean => {
    const transportationState = getBusinessAreaServicesState(state, 'transportation');
    return transportationState.services.length > 0 || transportationState.meta !== null;
};

/**
 * Check if business has ever had transportation services (prevents layout jumping)
 */
export const getTransportationHasEverHadServices = (state: RootState): boolean => {
    const transportationState = getBusinessAreaServicesState(state, 'transportation');
    const hasInitialized = getTransportationHasInitialized(state);
    return (
        transportationState.services.length > 0 ||
        (hasInitialized && (transportationState.meta?.totalServices || 0) > 0)
    );
};

/**
 * Check if transportation is empty (no services and has initialized)
 */
export const getTransportationIsEmpty = (state: RootState): boolean => {
    const transportationState = getBusinessAreaServicesState(state, 'transportation');
    const hasInitialized = getTransportationHasInitialized(state);
    return hasInitialized && transportationState.services.length === 0;
};

/**
 * Check if transportation is in first load state (hasn't initialized and is loading)
 */
export const getTransportationIsFirstLoad = (state: RootState): boolean => {
    const hasInitialized = getTransportationHasInitialized(state);
    const loading = getTransportationLoading(state);
    return !hasInitialized && loading;
};

// ===== TRAINING SPECIFIC SELECTORS =====

/**
 * Get training services (optimized for Training components)
 */
export const getTrainingServices = (state: RootState): Array<Service> => {
    return getServicesByBusinessArea(state, 'training');
};

/**
 * Get training meta (optimized for Training components)
 */
export const getTrainingMeta = (state: RootState): Meta | null => {
    return getMetaByBusinessArea(state, 'training');
};

/**
 * Get training loading state (optimized for Training components)
 */
export const getTrainingLoading = (state: RootState): boolean => {
    return getLoadingByBusinessArea(state, 'training');
};

/**
 * Check if training has initialized (has fetched data successfully at least once)
 */
export const getTrainingHasInitialized = (state: RootState): boolean => {
    const trainingState = getBusinessAreaServicesState(state, 'training');
    return trainingState.services.length > 0 || trainingState.meta !== null;
};

/**
 * Check if business has ever had training services (prevents layout jumping)
 */
export const getTrainingHasEverHadServices = (state: RootState): boolean => {
    const trainingState = getBusinessAreaServicesState(state, 'training');
    const hasInitialized = getTrainingHasInitialized(state);
    return trainingState.services.length > 0 || (hasInitialized && (trainingState.meta?.totalServices || 0) > 0);
};

/**
 * Check if training is empty (no services and has initialized)
 */
export const getTrainingIsEmpty = (state: RootState): boolean => {
    const trainingState = getBusinessAreaServicesState(state, 'training');
    const hasInitialized = getTrainingHasInitialized(state);
    return hasInitialized && trainingState.services.length === 0;
};

/**
 * Check if training is in first load state (hasn't initialized and is loading)
 */
export const getTrainingIsFirstLoad = (state: RootState): boolean => {
    const hasInitialized = getTrainingHasInitialized(state);
    const loading = getTrainingLoading(state);
    return !hasInitialized && loading;
};

// ===== BACKWARDS COMPATIBILITY SELECTORS =====

/**
 * BACKWARDS COMPATIBLE: Legacy selector for current business area services
 * This ensures existing code continues to work: `state.services.services`
 */
export const getCurrentServices = (state: RootState): Array<Service> => {
    return getServicesByBusinessArea(state, state.services.currentBusinessArea);
};

/**
 * BACKWARDS COMPATIBLE: Legacy selector for current business area meta
 */
export const getCurrentMeta = (state: RootState): Meta | null => {
    return getMetaByBusinessArea(state, state.services.currentBusinessArea);
};

/**
 * BACKWARDS COMPATIBLE: Legacy selector for current business area loading
 */
export const getCurrentLoading = (state: RootState): boolean => {
    return getLoadingByBusinessArea(state, state.services.currentBusinessArea);
};

// ===== GLOBAL SELECTORS =====

/**
 * EXISTING: Global status (unchanged for compatibility)
 */
export const getServicesStatus = (state: RootState) => {
    return state.services.status;
};

/**
 * EXISTING: Saved service (unchanged for compatibility)
 */
export const getSavedService = (state: RootState) => {
    return state.services.savedService;
};

/**
 * EXISTING: Duplicated service ID (unchanged for compatibility)
 */
export const getDuplicatedServiceId = (state: RootState) => {
    return state.services.duplicatedServiceId;
};
